/**
 * Configurable debug utility
 *
 * This utility can be instantiated with debug configuration and used
 * consistently across both server and client-side code.
 */

type DebugConfig = {
  isEnabled: boolean;
};

type CreateDebug = ReturnType<typeof createDebug>;

/**
 * Debug utility that only outputs when configured debug mode is enabled
 */
function createDebug(config: DebugConfig) {
  return {
    isEnabled: () => config.isEnabled,

    log: (message: string, ...args: unknown[]) => {
      if (config.isEnabled) {
        console.log(message, ...args);
      }
    },

    warn: (message: string, ...args: unknown[]) => {
      if (config.isEnabled) {
        console.warn(message, ...args);
      }
    },

    error: (message: string, ...args: unknown[]) => {
      if (config.isEnabled) {
        console.error(message, ...args);
      }
    },

    /**
     * Always log an error, regardless of debug mode
     */
    forceError: (message: string, ...args: unknown[]) => {
      console.error(message, ...args);
    },

    /**
     * Always log a warning, regardless of debug mode
     */
    forceWarn: (message: string, ...args: unknown[]) => {
      console.warn(message, ...args);
    },
  };
}

export { createDebug, type CreateDebug, type DebugConfig };
